export const ORDER_STATUS = {
  PLACED: 100, // Payment not received yet; a paid order moves on by itself.
  // The person checking payments has ticked the order (since 2026-10-29);
  // the warehouse packs from here.
  CONFIRMED: 101,
  // Paid, and moved here by the system the moment the payment landed
  // (2026-10-29); waiting for the payment check. The id is out of path
  // order because the earlier ids were already in customers' apps.
  PROCESSING: 107,
  // The parcel is with the shipping company, waybill attached. Same id as the
  // old "Out for delivery"; renamed 2026-10-21 to what staff actually record.
  HANDED_TO_SHIPPING: 102,
  DELIVERED: 103,
  CANCELLED: 104,
  /*
   * The parcel came back (added 2026-10-21): undelivered from the shipping
   * company, or sent back by the customer after delivery. Not an exit: a
   * returned order is sent out again (a wrong item, replaced) or cancelled.
   * The backend puts the lines back in stock on the way in and takes them
   * again on the way out; money is only settled by a cancel. The trip back
   * has its own tracking number, like every trip (`shipments`, saved through
   * admin/orders/shipments). Only a whole order can be returned, from any
   * paid stage on the path (never New Order or Hold): a returned order moved
   * on by mistake has to be able to come back (Mia, 2026-10-22).
   */
  RETURNED: 108,
  /*
   * "On hold" — a pause, not a stage and not an exit.
   *
   * Order-level only: the backend rejects it on admin/orders/detail-status for
   * everyone, super admins included ("Hold applies to the whole order"), so it
   * is filtered out of the per-item pickers rather than offered and refused.
   *
   * Placing or leaving hold is restricted to super admins, and while an order
   * is held it is fully read-only for everyone else — including edits that
   * carry no status change at all, like an address or a note.
   */
  ON_HOLD: 106,
};

/*
 * The happy path, in order — the stages an order walks through when nothing
 * goes wrong. This is business logic, so it lives in code; the *names* shown
 * for each stage come from `admin/statuses` (100 is titled "New Order", 102
 * "Handed to shipping company", 103 "Completed"), so the wording always matches
 * the rest of the UI without the sequence being guessable from the data.
 *
 * Anything not listed here — Cancelled, Returned, the retired Rejected, and any
 * status added later — is an exit rather than a stage. Exits never appear on
 * the progress track and are never proposed by the "Mark …" button; they are
 * reached deliberately from the ⋯ menu. Sorting the raw status list by id and
 * treating the last entry as "next" is what made a Completed order offer
 * "Mark Rejected" — and it is also why this list, not the ids, decides the
 * order of the tabs (107 sits between 100 and 101 on the path).
 *
 * Since 29 Oct 2026 a paid order moves to Under processing (107) on its
 * own, and the person checking payments ticks it to Confirmed (101) for the
 * warehouse to pack — so Under processing comes BEFORE Confirmed. (The
 * customer reads those two the other way round; the website and the app
 * handle that wording, the dashboard keeps its own names.)
 *
 * If the backend ever gains a genuine new stage, adding it here is the one
 * deliberate edit required.
 */
export const ORDER_LIFECYCLE_STATUSES = [
  ORDER_STATUS.PLACED,
  ORDER_STATUS.PROCESSING,
  ORDER_STATUS.CONFIRMED,
  ORDER_STATUS.HANDED_TO_SHIPPING,
  ORDER_STATUS.DELIVERED,
];

/*
 * The server's own transition rules, mirrored so the UI never offers a move
 * the API will refuse (see orderStatus.allowedTransitions).
 *
 *   · Hold is entered from — and resumed to — the pre-dispatch stages only.
 *   · A return needs a parcel that left: handed to shipping, or delivered.
 */
export const HOLDABLE_STATUSES = [
  ORDER_STATUS.PLACED,
  ORDER_STATUS.PROCESSING,
  ORDER_STATUS.CONFIRMED,
];
export const RETURNABLE_STATUSES = [
  ORDER_STATUS.PROCESSING,
  ORDER_STATUS.CONFIRMED,
  ORDER_STATUS.HANDED_TO_SHIPPING,
  ORDER_STATUS.DELIVERED,
];
/*
 * Where a returned order can go next, besides Cancelled: back onto the path
 * to be sent out again. Not New Order (unpaid) and not Hold (resume to
 * a paid stage first).
 */
export const RETURN_RESUME_STATUSES = [
  ORDER_STATUS.PROCESSING,
  ORDER_STATUS.CONFIRMED,
  ORDER_STATUS.HANDED_TO_SHIPPING,
  ORDER_STATUS.DELIVERED,
];

/*
 * The one order every status list on screen follows — tabs, the row picker,
 * the ⋯ menu, the bulk select, the per-item picker: the path as an order
 * walks it, then the pause, then the exits. The API returns statuses by id,
 * and ids are not path order (Processing is 107), so nothing may rely on the
 * API's order. A status not listed here lands after the known ones, by id,
 * so it is still reachable.
 */
export const STATUS_DISPLAY_ORDER = [
  ...ORDER_LIFECYCLE_STATUSES,
  ORDER_STATUS.ON_HOLD,
  ORDER_STATUS.CANCELLED,
  ORDER_STATUS.RETURNED,
];

export const statusRank = (statusId) => {
  const id = Number(statusId);
  const index = STATUS_DISPLAY_ORDER.indexOf(id);
  return index === -1 ? STATUS_DISPLAY_ORDER.length + id : index;
};

/*
 * payment_method is a stable enum. Tamara orders can never be edited — the
 * backend refuses every editing endpoint for them — so the UI has to know this
 * value rather than discovering it through a 422.
 */
export const PAYMENT_METHOD = {
  CARD: 1,
  TAMARA: 2,
  WALLET: 3,
  TABBY: 4,
};

/** Manual delivery fee bounds, matching the backend's validation. */
export const DELIVERY_CHARGE_MIN = 0;
export const DELIVERY_CHARGE_MAX = 1000;

/** A line's quantity range, matching the backend's validation. */
export const ITEM_QTY_MIN = 1;
export const ITEM_QTY_MAX = 999;

/* In-house delivery is off — fulfilment goes out through a courier, so there
 * are no Nysmah drivers to assign and every driver control would be an empty
 * dropdown. Nothing is deleted: this flag hides all of them at once.
 *
 * What it gates:
 *   - the "Driver" picker in OrderView's Order Actions card, and the getDriver
 *     fetch behind it
 *   - the Driver column (driverSelectCell) on the Out for Delivery tab
 *   - the "Driver waiting" / "Driver assigned" chips (orderNumberWithDriverCell)
 *   - OrderListScreen's includeDriver fetch, which the tab config drives
 *
 * Flip to true to bring the whole feature back in one edit. */
export const DRIVER_ASSIGNMENT_ENABLED = false;

// Catalogue prices are published VAT-inclusive: the price a shopper sees is the
// price they pay, tax already inside it. No VAT amount is calculated or stored
// anywhere, so order screens state the rate rather than a figure — a "SAR 0.00"
// next to VAT would read as "no tax was charged", the opposite of what the
// price means. The backend keeps its own copy for the invoice PDF and email
// (VAT_RATE_PERCENT in app/utils/constants.py); change both together.
export const VAT_RATE_PERCENT = 15;

export const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Tamex's public tracking page — appending the tracking number to /track/ lands
// straight on that shipment, no login or API involved (verified 2026-07-29
// with a live number). Used by the Tracking # column and OrderView's card.
export const tamexTrackUrl = (n) =>
  `https://tamex.com.sa/track/${encodeURIComponent(String(n).trim())}`;
